import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Smile, X, Lock } from "lucide-react";

/**
 * EmotePicker — floating popover with two tabs:
 *   1. Emojis      → curated unicode emoji, always available
 *   2. Stickers    → user's owned sticker packs (starter is free for everyone)
 *
 * onInsert(text) — called with the character/token to inject at the caret.
 *
 * Stickers are inserted as `<sticker:pack/key>` tokens which the chat
 * renderer parses back into the sticker character.
 */

// Curated unicode emoji categories. Native rendering; no backend needed.
const EMOJI_CATEGORIES = [
  { label: "Smileys",  emojis: ["😀","😂","🥲","😍","🤔","😎","😭","😴","🤯","🤖","🥳","😇","🤩","😜","🤨","😬","😅","🙄","😤","🥴"] },
  { label: "Hands",    emojis: ["👍","👎","👏","🙌","🤝","🙏","👋","🤙","👊","✊","🖖","🤞","🤟","🫡","🫰","👉","🤌","💪","🦾","✌️"] },
  { label: "Hearts",   emojis: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕","💖","💗","💘","💝","💞","💓","💌","♥️"] },
  { label: "Gaming",   emojis: ["🎮","🕹️","🎯","🏆","🥇","🥈","🥉","👑","🃏","🎲","♟️","⚡","🔥","💯","💥","💎","🚀","⭐","✨","🎉"] },
  { label: "Objects",  emojis: ["💰","🪙","💵","📺","📱","💻","🎧","🎤","📸","🎬","☕","🍿","🍕","🌮","🍔","🍩","🍺","🥤","🎁","🎈"] },
];

export default function EmotePicker({ open, onClose, onInsert }) {
  const [tab, setTab] = useState("emojis");
  const [inv, setInv] = useState({ owned_slugs: [], packs: [] });
  const [invLoading, setInvLoading] = useState(false);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    if (!open) return;
    setInvLoading(true);
    Promise.all([
      api.get("/emotes/inventory").catch(() => ({ data: { owned_slugs: [], packs: [] } })),
      api.get("/emotes/catalog").catch(() => ({ data: { packs: [] } })),
    ]).then(([iv, cat]) => {
      setInv(iv.data || { owned_slugs: [], packs: [] });
      setCatalog(cat.data?.packs || []);
    }).finally(() => setInvLoading(false));
  }, [open]);

  const ownedSet = useMemo(() => new Set(inv.owned_slugs || []), [inv.owned_slugs]);

  if (!open) return null;

  return (
    <div
      className="absolute bottom-full left-2 right-2 mb-2 z-40 bg-[#111114] border border-white/10 rounded-md shadow-2xl overflow-hidden"
      data-testid="emote-picker"
    >
      {/* Tabs */}
      <div className="flex items-center border-b border-white/10 bg-black/40">
        <PickerTab active={tab === "emojis"} onClick={() => setTab("emojis")} testid="emote-tab-emojis">Emojis</PickerTab>
        <PickerTab active={tab === "stickers"} onClick={() => setTab("stickers")} testid="emote-tab-stickers">Stickers</PickerTab>
        <button onClick={onClose} className="ml-auto px-2 py-1 text-zinc-500 hover:text-white" data-testid="emote-picker-close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="max-h-[280px] overflow-y-auto p-2">
        {tab === "emojis" && (
          <div className="space-y-3">
            {EMOJI_CATEGORIES.map(cat => (
              <div key={cat.label}>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1 px-1 sc-mono">{cat.label}</div>
                <div className="grid grid-cols-8 gap-1">
                  {cat.emojis.map(em => (
                    <button
                      key={em}
                      onClick={() => onInsert(em)}
                      className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-white/10 text-lg transition"
                      title={em}
                      data-testid={`emoji-${em}`}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "stickers" && (
          <div className="space-y-3">
            {invLoading && <div className="text-xs text-zinc-500 py-3 text-center">Loading your packs…</div>}
            {!invLoading && (inv.packs || []).length === 0 && (
              <div className="text-xs text-zinc-500 py-3 text-center" data-testid="emote-picker-empty">
                No packs yet. Visit the Shop to unlock more.
              </div>
            )}
            {!invLoading && (inv.packs || []).map(pack => (
              <div key={pack.slug} data-testid={`sticker-pack-${pack.slug}`}>
                <div className="flex items-center justify-between mb-1 px-1">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--sc-cyan)] sc-mono">{pack.name}</div>
                  <div className="text-[9px] text-emerald-400 sc-mono">OWNED</div>
                </div>
                <div className="grid grid-cols-6 gap-1">
                  {pack.stickers.map(st => (
                    <button
                      key={st.key}
                      onClick={() => onInsert(`<sticker:${pack.slug}/${st.key}>`)}
                      className="aspect-square flex items-center justify-center rounded-sm hover:bg-white/10 text-2xl transition"
                      title={st.label}
                      data-testid={`sticker-${pack.slug}-${st.key}`}
                    >
                      {st.char}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {/* Shop upsell — show first few unowned packs as a teaser only. */}
            {!invLoading && (() => {
              const teaser = catalog.filter(p => !ownedSet.has(p.slug)).slice(0, 2);
              if (teaser.length === 0) return null;
              return (
                <div className="pt-2 border-t border-white/5" data-testid="emote-picker-shop-teaser">
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 sc-mono px-1 mb-1 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Unlock more in the Shop
                  </div>
                  {teaser.map(pack => (
                    <div key={pack.slug} className="opacity-60 mb-2" data-testid={`sticker-pack-locked-${pack.slug}`}>
                      <div className="flex items-center justify-between mb-1 px-1">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-400 sc-mono">{pack.name}</div>
                        <div className="text-[9px] text-[color:var(--sc-volt)] sc-mono">{pack.cost_sparks} SPARKS</div>
                      </div>
                      <div className="grid grid-cols-6 gap-1 pointer-events-none">
                        {pack.stickers.slice(0, 6).map(st => (
                          <div key={st.key} className="aspect-square flex items-center justify-center text-xl grayscale">{st.char}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function PickerTab({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs uppercase tracking-widest sc-mono border-b-2 ${
        active
          ? "text-white border-[color:var(--sc-cyan)]"
          : "text-zinc-500 border-transparent hover:text-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}

export function EmoteButton({ onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-2 rounded-sm border border-white/10 hover:border-white/30 transition ${active ? "bg-white/10" : "bg-black/20"}`}
      title="Emojis & stickers"
      data-testid="chat-emote-button"
    >
      <Smile className="w-4 h-4 text-zinc-300" />
    </button>
  );
}
